'use client'

import React, { useEffect, useRef, useState } from 'react';
import { TimePicker } from 'antd';
import { formatMsg, timeutil } from 'sharedresources';
import { ParentSession, clampTime, getTimeBounds, getTimeError, toSessionTime } from './parentAttendance';
import styles from './style.module.css';

const fmtTime = (epoch: number) => timeutil.getInstance(epoch).format('h:mm A');

interface SheetProps {
    title: string;
    subtitle: string;
    onClose: () => void;
    children: React.ReactNode;
    testId: string;
}

/** Bottom sheet on phones, centred dialog on wider screens (see style.module.css). */
const Sheet = ({ title, subtitle, onClose, children, testId }: SheetProps) => {
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        dialogRef.current?.focus();
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                className={styles.sheet}
                onClick={(e) => e.stopPropagation()}
                data-testid={testId}
            >
                <div className={styles.sheetGrip} aria-hidden="true" />
                <div className={styles.sheetHead}>
                    <div>
                        <div className={styles.sheetTitle}>{title}</div>
                        <div className={styles.sheetSub}>{subtitle}</div>
                    </div>
                    <button type="button" className={styles.sheetClose} onClick={onClose} aria-label={formatMsg('parentAttendance.close')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

interface TimeSheetProps {
    session: ParentSession;
    field: 'in' | 'out';
    editable: boolean;
    now: number;
    title: string;
    subtitle: string;
    saving: boolean;
    onClose: () => void;
    onSave: (time: number) => void;
}

/**
 * Check in / check out. Opens at the current time. Only "Allow custom time" schools can change
 * it: any time up to now, and for check-out not before the recorded check-in.
 */
export const TimeSheet = ({ session, field, editable, now, title, subtitle, saving, onClose, onSave }: TimeSheetProps) => {
    const [value, setValue] = useState(() => clampTime(now, getTimeBounds(session, field, now)));
    const error = editable ? getTimeError(session, field, value, timeutil.getEpoch()) : null;

    const isToday = timeutil.getInstance(session.event.date).isSame(timeutil.getInstance(now), 'day');
    const dateLabel = `${isToday ? `${formatMsg('parentAttendance.today')}, ` : ''}${timeutil.getInstance(session.event.date).format('DD MMM')}`;

    const errorText = error === 'FUTURE'
        ? formatMsg('parentAttendance.errorFuture')
        : error === 'BEFORE_CHECK_IN'
            ? formatMsg('parentAttendance.errorBeforeCheckIn', { time: fmtTime(session.attendance?.latestCheckIn) })
            : null;

    return (
        <Sheet title={title} subtitle={subtitle} onClose={onClose} testId="parent-attendance-time-sheet">
            <div className={styles.dateRow}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B5B5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                {dateLabel}
            </div>
            {editable ? (
                <div data-testid="time-picker-editable">
                    <TimePicker
                        value={timeutil.getInstance(value)}
                        onChange={(picked: any) => picked && setValue(toSessionTime(session, field, picked.hours(), picked.minutes()))}
                        format="hh:mm A"
                        use12Hours
                        allowClear={false}
                        inputReadOnly
                        showNow
                        status={error ? 'error' : undefined}
                        className={styles.timeInput}
                        aria-label={formatMsg('parentAttendance.time')}
                        data-testid="parent-attendance-time-input"
                    />
                    {errorText && <div className={styles.timeError} role="alert" data-testid="parent-attendance-time-error">{errorText}</div>}
                </div>
            ) : (
                <div className={styles.timeLocked} data-testid="time-picker-locked">{fmtTime(value)}</div>
            )}
            <div className={styles.rule}>{formatMsg('parentAttendance.finalNote')}</div>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`} disabled={saving || !!error} onClick={() => onSave(value)} data-testid="parent-attendance-save-time">
                {formatMsg(field === 'in' ? 'parentAttendance.saveCheckIn' : 'parentAttendance.saveCheckOut', { time: fmtTime(value) })}
            </button>
        </Sheet>
    );
};

interface ConfirmSheetProps {
    title: string;
    subtitle: string;
    saving: boolean;
    onClose: () => void;
}

export const PresentSheet = ({ title, subtitle, saving, onClose, onConfirm }: ConfirmSheetProps & { onConfirm: () => void }) => (
    <Sheet title={title} subtitle={subtitle} onClose={onClose} testId="parent-attendance-present-sheet">
        <div className={styles.warn}>{formatMsg('parentAttendance.presentBody')}</div>
        <div className={styles.sheetActions}>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>{formatMsg('parentAttendance.cancel')}</button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnWide}`} disabled={saving} onClick={onConfirm} data-testid="parent-attendance-confirm-present">
                {formatMsg('parentAttendance.markPresent')}
            </button>
        </div>
    </Sheet>
);

const REASONS = ['parentAttendance.reasonUnwell', 'parentAttendance.reasonTravelling', 'parentAttendance.reasonAppointment', 'parentAttendance.reasonOther'];

export const AbsentSheet = ({ title, subtitle, askReason, saving, onClose, onConfirm }: ConfirmSheetProps & { askReason: boolean; onConfirm: (note: string | null) => void }) => {
    const [reason, setReason] = useState<string | null>(null);
    const [otherText, setOtherText] = useState('');
    const otherLabel = formatMsg('parentAttendance.reasonOther');
    const isOther = reason === otherLabel;
    // "Other" sends what the parent typed, or just "Other" if they left it empty.
    const note = isOther ? (otherText.trim() || otherLabel) : reason;
    return (
        <Sheet title={title} subtitle={subtitle} onClose={onClose} testId="parent-attendance-absent-sheet">
            {askReason && (
                <div>
                    <div className={styles.fieldLabel}>{formatMsg('parentAttendance.reason')}</div>
                    <div className={styles.quickRow}>
                        {REASONS.map((key) => {
                            const label = formatMsg(key);
                            return (
                                <button key={key} type="button" className={`${styles.quick} ${reason === label ? styles.quickOn : ''}`} aria-pressed={reason === label} onClick={() => setReason(reason === label ? null : label)}>
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                    {isOther && (
                        <textarea
                            className={styles.noteInput}
                            value={otherText}
                            onChange={(e) => setOtherText(e.target.value)}
                            maxLength={500}
                            rows={3}
                            autoFocus
                            placeholder={formatMsg('parentAttendance.reasonOtherPlaceholder')}
                            aria-label={formatMsg('parentAttendance.reasonOtherPlaceholder')}
                            data-testid="parent-attendance-other-reason"
                        />
                    )}
                </div>
            )}
            <div className={styles.warn}>{formatMsg('parentAttendance.absentBody')}</div>
            <div className={styles.sheetActions}>
                <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>{formatMsg('parentAttendance.cancel')}</button>
                <button type="button" className={`${styles.btn} ${styles.btnDanger} ${styles.btnWide}`} disabled={saving} onClick={() => onConfirm(askReason ? note : null)} data-testid="parent-attendance-confirm-absent">
                    {formatMsg('parentAttendance.markAbsent')}
                </button>
            </div>
        </Sheet>
    );
};
